import type { Status } from '@commissionsight/shared';
import { type ReactNode, useMemo, useState } from 'react';
import { flagLabel, statusClasses, statusDotClasses } from '../lib/status.js';

/** CommissionSight logo mark + wordmark. */
export function Logo({ className = '' }: { className?: string }) {
  return (
    <span className={`flex items-center gap-2.5 ${className}`}>
      <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect width="32" height="32" rx="8" fill="#0B0F0E" />
        <rect x="7" y="8" width="18" height="4.5" rx="2.25" fill="#34D399" />
        <rect x="7" y="13.75" width="13" height="4.5" rx="2.25" fill="#FBBF24" />
        <rect x="7" y="19.5" width="8" height="4.5" rx="2.25" fill="#FB7185" />
      </svg>
      <span className="brand text-lg">
        Commission<span className="text-emerald-500">Sight</span>
      </span>
    </span>
  );
}

/** A 24×24 stroke icon (Lucide-style). Pass a path `d`; inherits `currentColor`. */
export function Icon({
  path,
  size = 20,
  className = '',
  strokeWidth = 2,
}: {
  path: string;
  size?: number;
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={`shrink-0 ${className}`}
    >
      <path
        d={path}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Curated icon paths used across the app. */
export const ICONS = {
  dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
  alert:
    'M10.3 3.9 1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0zM12 9v4M12 17h.01',
  undo: 'M9 14 4 9l5-5M4 9h11a5 5 0 010 10h-3',
  users:
    'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM22 21v-2a4 4 0 00-3-3.9M16 3.1a4 4 0 010 7.8',
  trendDown: 'M22 17l-8.5-8.5-5 5L2 7M16 17h6v-6',
  sparkle: 'M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17l-1.9-5.1L4.5 10l5.6-1.4z',
  pie: 'M21.2 15.9A10 10 0 118.1 2.8M22 12A10 10 0 0012 2v10z',
  inbox:
    'M22 12h-6l-2 3h-4l-2-3H2M5.5 5.5 2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.5-6.5A2 2 0 0016.8 4H7.2a2 2 0 00-1.7 1.5z',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  search: 'M21 21l-4.3-4.3M11 19a8 8 0 100-16 8 8 0 000 16z',
} as const;

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-2xl border border-neutral-200/80 bg-white p-5 shadow-sm ring-1 ring-black/[0.02] transition dark:border-neutral-800 dark:bg-neutral-900 dark:ring-white/[0.03] ${className}`}
    >
      {children}
    </div>
  );
}

export function StatPill({ status, label }: { status: Status; label?: string }) {
  return (
    <span
      className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClasses(status)}`}
    >
      <span className={`h-2 w-2 rounded-full ${statusDotClasses(status)}`} />
      {label ?? status}
    </span>
  );
}

// Flags that signal an adverse event — rendered as a red alert chip, not neutral.
const ALERT_FLAGS = new Set(['CHARGEBACK']);

export function FlagChips({ flags }: { flags: string[] }) {
  if (flags.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span
          key={f}
          className={
            ALERT_FLAGS.has(f)
              ? 'rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/50 dark:text-rose-300'
              : 'rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
          }
        >
          {flagLabel(f)}
        </span>
      ))}
    </span>
  );
}

export function Button({
  children,
  onClick,
  type = 'button',
  disabled,
  variant = 'primary',
  size = 'md',
  className = '',
  loading = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  className?: string;
  loading?: boolean;
}) {
  const base =
    'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition disabled:opacity-50';
  const sizes = { md: 'px-3.5 py-2 text-sm', sm: 'px-2.5 py-1 text-xs' };
  const variants = {
    primary: 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 active:bg-emerald-800',
    ghost:
      'border border-neutral-300 text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800',
    danger: 'bg-rose-600 text-white shadow-sm hover:bg-rose-700 active:bg-rose-800',
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {loading && <Spinner className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />}
      {children}
    </button>
  );
}

/**
 * Modal confirmation for a destructive/irreversible action. Renders nothing when
 * `open` is false. The confirm button takes `tone` ('danger' for deletes).
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  tone = 'primary',
  loading = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <dialog
      open
      aria-modal="true"
      className="fixed inset-0 z-50 m-0 flex h-full w-full items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
    >
      <Card className="w-full max-w-md shadow-xl">
        <h3 className="text-lg font-semibold">{title}</h3>
        <div className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{message}</div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant={tone} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </Card>
    </dialog>
  );
}

/** Animated ring spinner; inherits color via currentColor. */
export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-20" />
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** Pulsing placeholder block. */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-md bg-neutral-200/70 dark:bg-neutral-800 ${className}`}
    />
  );
}

/** Centered loading state for a panel/section. */
export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-sm text-neutral-500">
      <span className="text-emerald-600">
        <Spinner />
      </span>
      {label}
    </div>
  );
}

/** A prominent banner shown when an account feature is paused by the operator. */
export function PausedBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
      <Icon path={ICONS.alert} size={18} className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

/** Amber "Estimated" pill — flags a $ figure that rests on an auto-seeded rate
 *  (not the agency's confirmed contracted rate), so it isn't yet audit-grade. */
export function EstimatedBadge({ className = '' }: { className?: string }) {
  return (
    <span
      title="Built on an auto-estimated rate — confirm your contracted rate to make it audit-grade."
      className={`inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 ${className}`}
    >
      Estimated
    </span>
  );
}

/** Prominent amber banner shown when commission owed includes auto-estimated
 *  rates — tells the user it isn't defensible until contracted rates are set. */
export function EstimateBanner({ children }: { children?: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
      <Icon path={ICONS.alert} size={18} className="mt-0.5 shrink-0" />
      <span>
        {children ?? (
          <>
            Commission owed includes <b>auto-estimated rates</b>. It's a signal to investigate, not
            an audit-grade figure — confirm your contracted rates on the Rates page to make it
            defensible.
          </>
        )}
      </span>
    </div>
  );
}

/** Friendly empty state: a muted icon in a soft circle, a title, and an optional hint. */
export function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: string;
  title: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500">
        <Icon path={icon} size={22} />
      </span>
      <p className="mt-3 text-sm font-medium text-neutral-600 dark:text-neutral-300">{title}</p>
      {hint && <p className="mt-1 max-w-xs text-xs text-neutral-400">{hint}</p>}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the form control is passed as children
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-neutral-700 dark:text-neutral-300">{label}</span>
      {children}
    </label>
  );
}

export const inputClass =
  'w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-neutral-700 dark:bg-neutral-950';

// --- Sortable table headers ---------------------------------------------------

export type SortDir = 'asc' | 'desc';

/**
 * Client-side table sort. Returns the rows sorted by the active column (stable,
 * null-safe, numbers numerically and strings naturally), plus the current key/dir
 * and a `toggle` that flips direction on the same column or selects a new one
 * (defaulting to descending — most grids want "biggest first"). Sorting only the
 * already-loaded rows is intentional: every grid is either small, server-capped,
 * or virtualized, so this never sorts an unbounded set.
 */
export function useSort<T>(rows: T[], initial?: { key: string; dir?: SortDir }) {
  const [sortKey, setSortKey] = useState<string | null>(initial?.key ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initial?.dir ?? 'desc');
  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const get = (r: T) => (r as Record<string, unknown>)[sortKey];
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      let c: number;
      if (av == null && bv == null) c = 0;
      else if (av == null) c = -1;
      else if (bv == null) c = 1;
      else if (typeof av === 'number' && typeof bv === 'number') c = av - bv;
      else c = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? c : -c;
    });
    return arr;
  }, [rows, sortKey, sortDir]);
  const toggle = (k: string) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir('desc');
    }
  };
  return { sorted, sortKey, sortDir, toggle };
}

/** A clickable, sortable `<th>`. `k` is the row field to sort by. */
export function SortHeader({
  label,
  k,
  sortKey,
  sortDir,
  onSort,
  align = 'left',
  className = '',
}: {
  label: ReactNode;
  k: string;
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (k: string) => void;
  align?: 'left' | 'right';
  className?: string;
}) {
  const active = sortKey === k;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 font-medium transition hover:text-neutral-800 dark:hover:text-neutral-200 ${align === 'right' ? 'flex-row-reverse' : ''}`}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        {label}
        <span
          className={`text-[9px] leading-none ${active ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-300 dark:text-neutral-600'}`}
          aria-hidden="true"
        >
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * A small (i) info icon that reveals a definition popover on hover/focus —
 * used next to metric labels to explain exactly what each number means and how
 * to act on it (e.g. how to raise owed coverage). Pure CSS reveal (group-hover /
 * focus-within) so it's keyboard-accessible and needs no state. The popover
 * escapes the card (no clipping — cards aren't overflow-hidden).
 */
export function InfoTip({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <span className="group relative inline-flex align-middle leading-none">
      <button
        type="button"
        aria-label={title ? `What is “${title}”?` : 'More information'}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-neutral-400 transition hover:text-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 dark:text-neutral-500 dark:hover:text-emerald-400"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
          <path
            d="M12 11v5M12 7.6h.01"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-6 z-50 w-64 max-w-[80vw] rounded-lg border border-neutral-200 bg-white p-3 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-neutral-600 opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
      >
        {title && (
          <span className="mb-1 block font-semibold text-neutral-800 dark:text-neutral-100">
            {title}
          </span>
        )}
        {children}
      </span>
    </span>
  );
}
